import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatchApp } from "../../../lib/redux";
import { startGetAdsCampaignsForGlobalReport } from "../../../actions/adsCampaigns";
import { useReportSnapshot } from "../../../features/reportPdf/ReportSnapshotContext";

const FIRST_REQUEST_DELAY_MS = 4000;

const getDateRangeKey = (dateRange) => {
  if (!dateRange?.startDateTime || !dateRange?.endDateTime) {
    return null;
  }
  return `${dateRange.startDateTime}|${dateRange.endDateTime}`;
};

/**
 * Loads Global Report RTDB candidates for the date range.
 * Final membership requires activity post-filter in useGlobalReportStatistics.
 */
const useGlobalReportData = (dateRange) => {
  const dispatch = useDispatchApp();
  const snapshot = useReportSnapshot();
  const [campaigns, setCampaigns] = useState(null);
  const [loadedDateRangeKey, setLoadedDateRangeKey] = useState(null);
  const fetchIdRef = useRef(0);
  const isInitialFetchRef = useRef(true);

  const startDateTime = dateRange?.startDateTime;
  const endDateTime = dateRange?.endDateTime;
  const dateRangeKey = useMemo(
    () => getDateRangeKey({ startDateTime, endDateTime }),
    [endDateTime, startDateTime]
  );

  const isLoadingCampaigns =
    !dateRangeKey || dateRangeKey !== loadedDateRangeKey;

  const fetchData = useCallback(async () => {
    if (snapshot || !dateRangeKey) {
      return;
    }

    const fetchId = ++fetchIdRef.current;
    const shouldDelayFirstRequest = isInitialFetchRef.current;
    if (shouldDelayFirstRequest) {
      isInitialFetchRef.current = false;
      await new Promise((resolve) =>
        setTimeout(resolve, FIRST_REQUEST_DELAY_MS)
      );
      if (fetchId !== fetchIdRef.current) {
        return;
      }
    }

    const campaignsResponse = await dispatch(
      startGetAdsCampaignsForGlobalReport(dateRange)
    );

    if (fetchId !== fetchIdRef.current) {
      return;
    }

    setCampaigns(campaignsResponse?.ok ? campaignsResponse.data : {});
    setLoadedDateRangeKey(dateRangeKey);
  }, [dateRange, dateRangeKey, dispatch, snapshot]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (snapshot) {
    return {
      campaigns: snapshot.campaigns || {},
      isLoadingCampaigns: false,
      reload: fetchData,
    };
  }

  return {
    campaigns: campaigns || {},
    isLoadingCampaigns,
    reload: fetchData,
  };
};

export default useGlobalReportData;
